import { type FC, type CSSProperties } from 'react';
import { useGameStore } from '@/stores/gameStore';

interface ItemDebugDisplayProps {
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  playerId?: string;
  isVisible?: boolean;
}

const ItemDebugDisplay: FC<ItemDebugDisplayProps> = ({
  position = 'bottom-left',
  playerId = 'current-player',
  isVisible = true
}) => {
  const { players, gameState, characterSpeedData, selectedBonusItem } = useGameStore();

  const player = players.find(p => p.id === playerId);

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  const getPositionStyles = () => {
    const baseStyles: CSSProperties = {
      position: 'fixed',
      zIndex: 9999,
      padding: '1rem',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      color: '#fff',
      borderRadius: '8px',
      fontSize: '0.75rem',
      fontFamily: 'monospace',
      border: '1px solid #444',
      minWidth: '250px',
      maxHeight: '300px',
      overflow: 'auto'
    };

    switch (position) {
      case 'top-left':
        return { ...baseStyles, top: '10px', left: '10px' };
      case 'top-right':
        return { ...baseStyles, top: '10px', right: '10px' };
      case 'bottom-left':
        return { ...baseStyles, bottom: '10px', left: '10px' };
      case 'bottom-right':
        return { ...baseStyles, bottom: '10px', right: '10px' };
      default:
        return { ...baseStyles, bottom: '10px', left: '10px' };
    }
  };

  if (!player) {
    return (
      <div style={getPositionStyles()}>
        <div style={{ color: '#ff6b6b', fontWeight: 'bold' }}>
          🚨 Player not found: {playerId}
        </div>
      </div>
    );
  }

  const activeModifiers = characterSpeedData?.modifiers || [];
  const currentSpeed = characterSpeedData?.currentEffectiveSpeed || 0;
  const baseSpeed = characterSpeedData?.baseSpeed || 0;

  return (
    <div style={getPositionStyles()}>
      <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#4CAF50' }}>
        🔍 ITEM DEBUG ({playerId})
      </div>

      <div style={{ marginBottom: '0.5rem' }}>
        <span style={{ color: '#FFD700' }}>State:</span> {gameState}
      </div>

      <div style={{ marginBottom: '0.5rem' }}>
        <span style={{ color: '#FFD700' }}>Selected Item:</span> {selectedBonusItem?.name || 'None'}
      </div>

      <div style={{ marginBottom: '0.5rem' }}>
        <span style={{ color: '#FFD700' }}>Player Items:</span> {player.items?.length || 0}
        {player.items?.map((item, i) => (
          <div key={i} style={{ paddingLeft: '1rem', color: '#81C784' }}>
            • {item.name} ({item.type})
          </div>
        ))}
      </div>

      <div style={{ marginBottom: '0.5rem' }}>
        <span style={{ color: '#FFD700' }}>Item Flags:</span>
        <div style={{ paddingLeft: '1rem' }}>
          <div style={{ color: player.shoesEffectNextRound ? '#4CAF50' : '#666' }}>
            👟 Shoes Next Round: {player.shoesEffectNextRound ? 'YES' : 'no'}
          </div>
          <div style={{ color: player.turboAvailable ? '#4CAF50' : '#666' }}>
            ⚡ Turbo Available: {player.turboAvailable ? 'YES' : 'no'}
          </div>
          <div style={{ color: player.turboUsed ? '#FF5722' : '#666' }}>
            🔥 Turbo Used: {player.turboUsed ? 'YES' : 'no'}
          </div>
          <div style={{ color: (player.netCharges || 0) > 0 ? '#4CAF50' : '#666' }}>
            🕸️ Net Charges: {player.netCharges || 0}
          </div>
          <div style={{ color: (player.grenadeCharges || 0) > 0 ? '#FF6B35' : '#666' }}>
            💣 Grenade Charges: {player.grenadeCharges || 0}
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '0.5rem' }}>
        <span style={{ color: '#FFD700' }}>Speed:</span>
        <div style={{ paddingLeft: '1rem' }}>
          <div>Base: {baseSpeed.toFixed(1)}</div>
          <div>Current: {currentSpeed.toFixed(1)}</div>
          <div>Modifiers: {activeModifiers.length}</div>
        </div>
      </div>

      {activeModifiers.length > 0 && (
        <div style={{ marginBottom: '0.5rem' }}>
          <span style={{ color: '#FFD700' }}>Active Modifiers:</span>
          {activeModifiers.map((mod, i) => (
            <div key={i} style={{ paddingLeft: '1rem', color: '#90CAF9', fontSize: '0.7rem' }}>
              • {mod.source}: {mod.multiplier}x ({Math.ceil((mod.startTime + mod.duration - Date.now()) / 1000)}s left)
            </div>
          ))}
        </div>
      )}

      <div style={{ fontSize: '0.6rem', color: '#999', marginTop: '0.5rem' }}>
        Last updated: {new Date().toLocaleTimeString()}
      </div>
    </div>
  );
};

export default ItemDebugDisplay;